import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from 'sonner';
import { Box, Plus, Pencil, Trash2, GripVertical } from 'lucide-react';

export default function IlotsPage() {
  const [ilots, setIlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIlot, setEditingIlot] = useState(null);
  const [formData, setFormData] = useState({ name: '', width: 300, display_order: 0 });

  useEffect(() => {
    fetchIlots();
  }, []);

  const fetchIlots = async () => {
    try {
      const response = await fetch(`${API}/ilots`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setIlots(data);
      }
    } catch (error) {
      console.error('Failed to fetch ilots:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingIlot(null);
    setFormData({ name: '', width: 300, display_order: ilots.length });
    setDialogOpen(true);
  };

  const handleOpenEdit = (ilot) => {
    setEditingIlot(ilot);
    setFormData({ name: ilot.name, width: ilot.width, display_order: ilot.display_order });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingIlot ? `${API}/ilots/${editingIlot.ilot_id}` : `${API}/ilots`;
      const method = editingIlot ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingIlot ? 'Îlot mis à jour' : 'Îlot créé');
        setDialogOpen(false);
        fetchIlots();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDelete = async (ilot) => {
    if (!window.confirm(`Supprimer l'îlot "${ilot.name}" et toutes ses machines ?`)) return;
    
    try {
      const response = await fetch(`${API}/ilots/${ilot.ilot_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Îlot supprimé');
        fetchIlots();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="ilots-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ilots-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight uppercase">
            Gestion des Îlots
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm uppercase tracking-wider">
            Configurez vos îlots de production
          </p>
        </div>
        <Button onClick={handleOpenCreate} className="btn-industrial rounded-sm" data-testid="create-ilot-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvel Îlot
        </Button>
      </div>

      {/* Ilots Table */}
      <Card className="border-2 rounded-sm">
        <CardContent className="p-0">
          {ilots.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Box className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
              <h3 className="font-heading font-semibold text-xl uppercase tracking-tight mb-2">
                Aucun îlot
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                Créez votre premier îlot de production
              </p>
              <Button onClick={handleOpenCreate} className="btn-industrial rounded-sm" data-testid="create-first-ilot-btn">
                <Plus className="w-4 h-4 mr-2" />
                Créer un îlot
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b-2">
                  <TableHead className="industrial-label">Ordre</TableHead>
                  <TableHead className="industrial-label">Nom</TableHead>
                  <TableHead className="industrial-label">Largeur</TableHead>
                  <TableHead className="industrial-label">Date de création</TableHead>
                  <TableHead className="industrial-label text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ilots.map((ilot, index) => (
                  <TableRow key={ilot.ilot_id} className="border-b" data-testid={`ilot-row-${ilot.ilot_id}`}>
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-4 h-4 text-muted-foreground" />
                        {ilot.display_order + 1}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center">
                          <Box className="w-4 h-4 text-primary" strokeWidth={1.5} />
                        </div>
                        <span className="font-medium">{ilot.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{ilot.width}px</TableCell>
                    <TableCell className="font-mono text-sm">
                      {new Date(ilot.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(ilot)}
                          data-testid={`edit-ilot-${ilot.ilot_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(ilot)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-ilot-${ilot.ilot_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm border-2">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-xl uppercase tracking-tight">
              {editingIlot ? 'Modifier l\'îlot' : 'Nouvel îlot'}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs uppercase tracking-wider">
              {editingIlot ? 'Modifiez les informations de l\'îlot' : 'Créez un nouvel îlot de production'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="ilot-name" className="industrial-label">Nom de l'îlot</Label>
                <Input
                  id="ilot-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Îlot A"
                  className="rounded-sm border-2"
                  required
                  data-testid="ilot-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ilot-width" className="industrial-label">Largeur (px)</Label>
                  <Input
                    id="ilot-width"
                    type="number"
                    value={formData.width}
                    onChange={(e) => setFormData({ ...formData, width: parseInt(e.target.value) || 300 })}
                    min={100}
                    max={1000}
                    className="rounded-sm border-2"
                    data-testid="ilot-width-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ilot-order" className="industrial-label">Ordre d'affichage</Label>
                  <Input
                    id="ilot-order"
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    min={0}
                    className="rounded-sm border-2"
                    data-testid="ilot-order-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm border-2">
                Annuler
              </Button>
              <Button type="submit" className="btn-industrial rounded-sm" data-testid="save-ilot-btn">
                {editingIlot ? 'Enregistrer' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
